import {
  format,
  formatISO,
  getDay,
  isValid,
  parseISO,
  setHours,
  setMinutes,
  setSeconds
} from 'date-fns';

import {
  DATE_PICKER_DATE_FORMAT,
  HOURS_MINUTES_TIME_FORMAT
} from '@nw/shared/config';

import {
  AVATAR,
  DEFAULT,
  DEFAULT_DATE,
  DEFAULT_VALUE,
  EMAIL,
  EXTRA_USER_INFO_BASED_ON_PRODUCT,
  FIRST_NAME,
  FORM_SELECT_FIELD_OPTION_SPECIFIC_VALUE,
  FORM_VALUE,
  FORM_VALUES_DATE,
  FORM_VALUES_STRING,
  FORMATTED_DATE,
  FORMATTED_TIME,
  FRONTEND_PRODUCT_PATH_ID,
  GENDER,
  INTENSIVE_DEMO_PREFERRED_TIME,
  INVITE_CODE,
  LAST_NAME,
  OCCUPATION,
  PARAMS,
  PHONE,
  PREFERRED_SLOT_AND_DATE,
  PRODUCT_CODE,
  SELECT,
  SPOT_REGISTERED_DAY,
  STATE_OF_RESIDENCE,
  USER_PREFERRED_DATE,
  WEBINAR_DAY,
  WEBINAR1_DATE_TIME,
  WHATSAPP_CONSENT
} from '../constants/SegmentFieldConstants';

import {
  getWebinarDateTimeFromSelectedSlot,
  getWeekDay
} from './DateTimeUtils';
import { getFrontEndFormPathId } from './MiscellaneousUtils';

const getFormattedDate = ({ hours, minutes, amOrPM }) => {
  return `${hours}:${minutes} ${amOrPM}`;
};
const getSelectedOption = (value, options) => {
  return options.find((eachOption) => eachOption.formValue === value);
};

const getFormattedValue = (type: 'INTEGER' | 'STRING', value) => {
  if (value)
    switch (true) {
      case type === 'INTEGER':
        return parseInt(value);
      case type === 'STRING':
        return value;
      default:
        return value;
    }
  return value;
};

//FIXME:Give Types to Args
export const segmentFieldValues = (
  eventField,
  profileData,
  requestObject,
  params,
  formValues = {},
  properties
) => {
  const {
    field,
    key,
    type,
    dependentField,
    extraInfoDependentField,
    dependentFormField,
    matchValue,
    dateFormat = DATE_PICKER_DATE_FORMAT,
    dependentSegmentField,
    timeFormat = HOURS_MINUTES_TIME_FORMAT,
    preferredSlotFormFieldKey,
    preferredDateFormFieldKey,
    webinarTime,
    fieldValue,
    breakTime,
    options,
    segmentValueType,
    defaultValue
  } = eventField;
  const {
    product_code,
    invite_code,
    user_details,
    extra_info_based_on_product_config
  } = requestObject;
  const extraInfoBasedOnProductConfig = extra_info_based_on_product_config
    ? JSON.parse(extra_info_based_on_product_config)
    : {};
  switch (true) {
    case type === FIRST_NAME:
      return user_details.full_name || profileData.name;
    case type === LAST_NAME:
      return user_details.lastName || profileData.lastName;
    case type === GENDER:
      return user_details.gender || profileData.gender;
    case type === AVATAR:
      return profileData.profilePicUrl;
    case type === PHONE:
      return profileData.phoneNumber;
    case type === EMAIL:
      return user_details.email || profileData.email;
    case type === STATE_OF_RESIDENCE: {
      return user_details.state_of_residence || profileData.stateOfResidence;
    }
    case type === OCCUPATION:
      return user_details.occupation || profileData.occupation;
    case type === PRODUCT_CODE:
      return product_code;
    case type === PARAMS: {
      return params.get(field);
    }
    case type === INVITE_CODE:
      return invite_code ? invite_code : '';
    case type === WEBINAR1_DATE_TIME: {
      const preferredSlot =
        extraInfoBasedOnProductConfig[extraInfoDependentField as string];
      let webinar1DateTime: any =
        formValues[dependentFormField as string] ??
        extraInfoBasedOnProductConfig[dependentFormField as string];

      const webinarDateTime =
        webinar1DateTime && preferredSlot
          ? getWebinarDateTimeFromSelectedSlot(webinar1DateTime, preferredSlot)
          : null;
      return webinarDateTime;
    }
    case type === EXTRA_USER_INFO_BASED_ON_PRODUCT:
      return extraInfoBasedOnProductConfig[key as string];
    case type === WHATSAPP_CONSENT:
      return user_details?.i_want_to_receive_updates_directly_on_whatsapp;
    case type === SPOT_REGISTERED_DAY:
      return params.get(dependentField) === 'TRUE'
        ? format(new Date(), 'EEEE')
        : undefined;
    case type === FORM_SELECT_FIELD_OPTION_SPECIFIC_VALUE:
      const formSelectedOptions = extraInfoBasedOnProductConfig[key as string];
      const formSelectedOption = formSelectedOptions.find(
        (eachOption) => eachOption.value === matchValue
      );
      return Boolean(formSelectedOption);
    case type === USER_PREFERRED_DATE:
      return format(
        parseISO(extraInfoBasedOnProductConfig[key as string]),
        'do MMMM'
      );
    case type === WEBINAR_DAY:
      return getWeekDay(
        getDay(parseISO(extraInfoBasedOnProductConfig[key as string]))
      );
    case type === FORMATTED_DATE: {
      const isValidDate =
        dependentSegmentField &&
        isValid(parseISO(properties[dependentSegmentField]));
      if (isValidDate) {
        return format(parseISO(properties[dependentSegmentField]), dateFormat);
      } else {
        throw Error(`Value of ${dependentSegmentField} should be a valid date`);
      }
    }
    case type === FORMATTED_TIME: {
      const isValidDate =
        dependentSegmentField &&
        isValid(parseISO(properties[dependentSegmentField]));
      if (isValidDate) {
        return format(parseISO(properties[dependentSegmentField]), timeFormat);
      } else {
        throw Error(`Value of ${dependentSegmentField} should be a valid date`);
      }
    }
    case type === FORM_VALUES_DATE: {
      const dateValue = extraInfoBasedOnProductConfig[key];
      if (dateValue) return format(parseISO(dateValue as string), dateFormat);
      else return null;
    }
    case type === PREFERRED_SLOT_AND_DATE: {
      const preferredDateSlotValue =
        extraInfoBasedOnProductConfig[preferredDateFormFieldKey];
      const preferredTimeSlotValue =
        extraInfoBasedOnProductConfig[preferredSlotFormFieldKey];

      if (preferredDateSlotValue && preferredTimeSlotValue) {
        let preferredWebinarDate = parseISO(
          format(new Date(preferredDateSlotValue), 'yyyy-MM-dd')
        );
        const preferredWebinarTimeArray = preferredTimeSlotValue.split(' - ');
        const preferredString = preferredWebinarTimeArray[0];
        const subStringSplitIndex =
          preferredString.length === 6
            ? 4
            : preferredString.length === 4
              ? 2
              : 1;
        const preferredSlotAMOrPMvalue = preferredWebinarTimeArray[0].substring(
          subStringSplitIndex,
          preferredWebinarTimeArray[0].length
        );
        if (preferredSlotAMOrPMvalue === 'AM') {
          preferredWebinarDate = setHours(
            preferredWebinarDate,
            parseInt(
              preferredWebinarTimeArray[0].substring(0, subStringSplitIndex)
            )
          );
          preferredWebinarDate = setMinutes(preferredWebinarDate, 0);
          preferredWebinarDate = setSeconds(preferredWebinarDate, 0);
        } else if (preferredSlotAMOrPMvalue === 'PM') {
          preferredWebinarDate = setHours(
            preferredWebinarDate,
            parseInt(
              preferredWebinarTimeArray[0].substring(0, subStringSplitIndex)
            ) + 12
          );
          if (subStringSplitIndex === 4) {
            preferredWebinarDate = setMinutes(
              preferredWebinarDate,
              parseInt(preferredWebinarTimeArray[0].substring(2, 4))
            );
            preferredWebinarDate = setSeconds(preferredWebinarDate, 0);
          } else {
            preferredWebinarDate = setMinutes(preferredWebinarDate, 0);
            preferredWebinarDate = setSeconds(preferredWebinarDate, 0);
          }
        }
        return formatISO(preferredWebinarDate);
      } else return null;
    }
    case type === INTENSIVE_DEMO_PREFERRED_TIME: {
      const demoPreferredTimeSlot = extraInfoBasedOnProductConfig[key];
      if (demoPreferredTimeSlot) {
        const demoPreferredTimeSlotArray = demoPreferredTimeSlot.split(' - ');
        const startTime = demoPreferredTimeSlotArray[0];
        if (startTime.length === 4) {
          return getFormattedDate({
            hours: startTime.substring(0, 2),
            minutes: '00',
            amOrPM: startTime.substring(2, 4)
          });
        } else if (startTime.length === 3) {
          return getFormattedDate({
            hours: startTime.substring(0, 1),
            minutes: '00',
            amOrPM: startTime.substring(1, 3)
          });
        } else {
          return startTime;
        }
      } else return null;
    }
    case type === FRONTEND_PRODUCT_PATH_ID: {
      return getFrontEndFormPathId();
    }
    case type === FORM_VALUES_STRING:
    case type === FORM_VALUE:
      return getFormattedValue(segmentValueType, formValues[key]);
    case type === DEFAULT:
      return fieldValue;
    case type === DEFAULT_DATE:
      return parseISO(fieldValue);
    case type === SELECT: {
      const formValue = formValues[key];
      const selectedOption = getSelectedOption(formValue, options);
      if (selectedOption) {
        return selectedOption.segmentValue;
      } else {
        return getFormattedValue(segmentValueType, formValue);
      }
    }
    case type === DEFAULT_VALUE: {
      return defaultValue;
    }
    default:
      throw new Error(`${type} is not defined`);
  }
};

export const getSegmentPropertiesObject = (
  segmentInfo,
  profileData,
  requestObject,
  params,
  formValues: any = {}
) => {
  const { eventFields } = segmentInfo;
  const properties = {};
  Object.keys(eventFields).forEach((index) => {
    properties[eventFields[index].field] = segmentFieldValues(
      eventFields[index],
      profileData,
      requestObject,
      params,
      formValues,
      properties
    );
  });
  return properties;
};
